import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { HttpClient } from "@angular/common/http";
import { environment } from '@environments/environment';
import { Provider, ProviderTaxonomy } from '@models/provider.model';
import { CreateUpdateProviderData, ProviderUploadResults } from '@store/actions/clinician-details/clinician-details.actions';

export interface NpiLookupResult {
  firstName: string | null;
  lastName: string | null;
  organizationName: string | null;
  enumerationType: string;
  primaryTaxonomyCode: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ClinicianDetailsService {
  private apiUrl = environment.apiUrl;

  getProviders(organizationId: number): Observable<Provider[]> {
    return this.http.get<Provider[]>(`${this.apiUrl}/providers/${organizationId}`);
  }

  getProviderTaxonomies(): Observable<ProviderTaxonomy[]> {
    return this.http.get<ProviderTaxonomy[]>(`${this.apiUrl}/providers/taxonomies`);
  }

  createProvider(providerData: CreateUpdateProviderData): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/providers/create`, providerData);
  }

  updateProvider(providerId: number, providerData: CreateUpdateProviderData): Observable<Provider> {
    return this.http.put<Provider>(`${this.apiUrl}/providers/${providerId}`, providerData);
  }

  lookupNpi(npi: string): Observable<NpiLookupResult> {
    return this.http.get<NpiLookupResult>(`${this.apiUrl}/providers/npi-lookup/${npi}`);
  }

  validateNPI(npi: string, organizationId: number, excludeProviderId?: number): Observable<boolean> {
    if (!npi) {
      return of(false);
    }
    const params: any = { npi: npi };
    if (excludeProviderId) {
      params.excludeProviderId = excludeProviderId;
    }
    return this.http.get<{ isAvailable: boolean }>(`${this.apiUrl}/providers/validate-npi/${organizationId}`, { params })
      .pipe(
        map(response => response.isAvailable),
        catchError(() => of(false))
      );
  }

  linkUserToProvider(organizationProviderId: number, userId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/providers/${organizationProviderId}/link-user`, {
       UserID: userId.toString() 
    });
  }

  unlinkUserFromProvider(organizationProviderId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/providers/${organizationProviderId}/unlink-user`, {});
  }

  deactivateProvider(organizationProviderId: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/providers/${organizationProviderId}/deactivate`, {});
  }

  uploadProviders(formData: FormData): Observable<ProviderUploadResults> {
    const organizationId = formData.get('organizationId');
    return this.http.post<ProviderUploadResults>(`${this.apiUrl}/providers/upload?organizationId=${organizationId}`, formData);
  }

  constructor(private http: HttpClient) { }
}